import { useState, useEffect } from 'react';
import Cookies from 'js-cookie';
import { Icon } from '../ui/Icon';
import { t } from '../../i18n/t';
import i18n from '../../i18n/config';

type CookieChoice = 'true' | 'false' | undefined;

export function CookieConsent() {
  // Start as null to avoid a flash: null = not yet determined, false = hide banner, true = show banner
  const [showConsent, setShowConsent] = useState<boolean | null>(null);
  const [currentChoice, setCurrentChoice] = useState<CookieChoice>(
    () => Cookies.get('cookieConsent') as CookieChoice
  );

  useEffect(() => {
    setShowConsent(!Cookies.get('cookieConsent'));
  }, []);

  const openSettings = () => {
    setCurrentChoice(Cookies.get('cookieConsent') as CookieChoice);
    setShowConsent(true);
  };

  const acceptCookies = () => {
    Cookies.set('cookieConsent', 'true', { expires: 365 });
    setCurrentChoice('true');
    setShowConsent(false);
  };

  const declineCookies = () => {
    Cookies.set('cookieConsent', 'false', { expires: 365 });
    setCurrentChoice('false');
    setShowConsent(false);
  };

  // Nothing rendered until we've read the cookie on the client
  if (showConsent === null) return null;

  const locale = i18n.locale || 'lv';
  const declineSelected = currentChoice === 'false';
  const acceptSelected = currentChoice === 'true';

  return (
    <>
      {/* Settings toggle — sits above StickyCta on mobile (bottom-[4.5rem]) and at bottom on desktop */}
      {!showConsent && (
        <button
          type="button"
          onClick={openSettings}
          className="fixed bottom-[4.5rem] lg:bottom-0 left-1/2 -translate-x-1/2 flex items-center gap-1 md:gap-2 px-2 py-1 md:px-4 md:py-2 text-xs md:text-sm font-medium text-gray-700 bg-white border border-b-0 border-gray-300 rounded-t-md md:rounded-t-lg shadow-[0_-2px_8px_rgba(0,0,0,0.08)] hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500 z-40"
          aria-label={t('cookie_consent.settings')}
        >
          <Icon name="chevron-down" className="h-3 w-3 md:h-4 md:w-4 shrink-0 rotate-180" />
          <Icon name="layers" className="h-3 w-3 md:h-4 md:w-4 shrink-0" />
          <span className="hidden md:inline">{t('cookie_consent.settings')}</span>
        </button>
      )}

      {/* Consent banner */}
      {showConsent && (
        <div className="fixed bottom-0 left-0 right-0 bg-white shadow-lg border-t border-gray-200 p-4 z-50">
          <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex-1">
              <p className="text-sm text-gray-600">
                {t('cookie_consent.message')}{' '}
                <a
                  href={`https://kreditiem.lv/documents/${locale}/privacy_policy.pdf`}
                  className="text-blue-600 hover:text-blue-800 ml-1 underline"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {t('cookie_consent.privacy_policy')}
                </a>
              </p>
            </div>
            <div className="flex gap-4">
              <button
                type="button"
                onClick={declineCookies}
                aria-pressed={declineSelected}
                className={`px-4 py-2 text-sm font-medium rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 ${
                  declineSelected
                    ? 'text-gray-900 bg-gray-100 border-2 border-gray-400 ring-2 ring-blue-500'
                    : 'text-gray-700 bg-white border border-gray-300 hover:bg-gray-50'
                }`}
              >
                {t('cookie_consent.decline')}
              </button>
              <button
                type="button"
                onClick={acceptCookies}
                aria-pressed={acceptSelected}
                className={`px-4 py-2 text-sm font-medium rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 ${
                  acceptSelected
                    ? 'text-white bg-blue-600 border-2 border-blue-600 ring-2 ring-blue-500'
                    : 'text-gray-700 bg-white border border-gray-300 hover:bg-gray-50'
                }`}
              >
                {t('cookie_consent.accept')}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
